import { useEffect, useRef } from 'react';
import { METER_FLOOR, type LevelSource } from './element-levels.js';
import { t } from './i18n.js';

const SCALE = [0, -6, -12, -18, -24, -36, -48];
const pct = (db: number) => `${((db - METER_FLOOR) / -METER_FLOOR) * 100}%`;

/**
 * A stereo VU meter of what `levels` reads (dBFS, green to −12, amber to −3, red above, with a falling peak mark),
 * drawn a frame at a time: upright beside a picture, or lying (`horizontal`) under a recorder's buttons. A new `reset`
 * drops the peak marks (a new clip).
 */
export function VuMeter({ levels, reset, horizontal = false, className = '' }: { levels: LevelSource; reset?: unknown; horizontal?: boolean; className?: string }) {
  const barL = useRef<HTMLDivElement>(null);
  const barR = useRef<HTMLDivElement>(null);
  const peakL = useRef<HTMLDivElement>(null);
  const peakR = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let raf = 0;
    const holds = [METER_FLOOR, METER_FLOOR];
    const draw = (bar: HTMLDivElement | null, peak: HTMLDivElement | null, db: number, i: 0 | 1) => {
      const hold = (holds[i] = Math.max(db, holds[i]! - 0.6));
      if (bar) {
        if (horizontal) bar.style.width = pct(db);
        else bar.style.height = pct(db);
        bar.dataset.level = db > -3 ? 'hot' : db > -12 ? 'warm' : 'ok';
      }
      if (peak) {
        if (horizontal) peak.style.left = pct(hold);
        else peak.style.bottom = pct(hold);
      }
    };
    const tick = () => {
      const [l, r] = levels.peaks();
      draw(barL.current, peakL.current, l, 0);
      draw(barR.current, peakR.current, r, 1);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [levels, reset, horizontal]);
  return (
    <div className={`mamp-vu${horizontal ? ' h' : ''} ${className}`.trim()} title={t('mamVu')}>
      <div className="mamp-vu-scale">
        {SCALE.map((db) => (
          <span key={db} style={horizontal ? { left: pct(db) } : { bottom: pct(db) }}>
            {db}
          </span>
        ))}
      </div>
      <div className="mamp-vu-ch">
        <div ref={barL} className="mamp-vu-bar" />
        <div ref={peakL} className="mamp-vu-peak" />
      </div>
      <div className="mamp-vu-ch">
        <div ref={barR} className="mamp-vu-bar" />
        <div ref={peakR} className="mamp-vu-peak" />
      </div>
    </div>
  );
}
